import { useMemo } from "react";

import { useGetClinicsQuery } from "services/clinics";

export type ClinicFilterOption = {
  label: string;
  value: string;
  region: number | undefined;
};

const CLINIC_LOCK_QUERY = "{id,name,region{id}}";

/**
 * The scoped clinics as filter options, for seeding a clinic-scoped filter.
 *
 * Restricting the picker's options is not enough on its own: an unseeded filter sends no clinic at
 * all, so the report would return every clinic while the UI implies it is scoped. Seeding the
 * scoped clinics is what makes the request match what the admin configured.
 *
 * Returns `null` until the clinics resolve, so seeding waits rather than writing a partial value.
 */
export default function useClinicOptionsForLock(
  allowedClinicIds?: string[]
): ClinicFilterOption[] | null {
  const { data: clinics = [], isLoading } = useGetClinicsQuery(
    { query: CLINIC_LOCK_QUERY },
    { skip: !allowedClinicIds?.length }
  );

  return useMemo(() => {
    if (!allowedClinicIds?.length) return null;
    if (isLoading || !clinics.length) return null;

    const options = clinics
      .filter((clinic) => allowedClinicIds.includes(String(clinic.id)))
      .map((clinic) => ({
        label: clinic.name,
        value: String(clinic.id),
        region: clinic.region?.id
      }));

    return options.length ? options : null;
  }, [allowedClinicIds, clinics, isLoading]);
}
